import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Simple Reveal on Scroll
const RevealOnScroll = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const CaseStudiesPage = () => {
  const caseStudies = [
    { slug: 'findyourfit', client: 'FINDYOURFIT', tagline: 'Making Wellness Social', image: '/find-your-fit-event.jpg', industry: 'WELLNESS & FITNESS' },
    { slug: 'goat', client: 'GOAT SILIGURI', tagline: 'Elevated Dining Experience', image: '/goat-new.jpg', industry: 'HOSPITALITY' },
    { slug: 'upre', client: 'UPRÉ BY 1559 AD', tagline: 'Premium Rooftop Dining', image: '/upre-lakeside.jpg', industry: 'HOSPITALITY' },
    { slug: 'ouzo', client: 'OUZO BAR & KITCHEN', tagline: 'Contemporary Nightlife', image: '/ouzo-chef-1.jpg', industry: 'HOSPITALITY' },
    { slug: 'house-of-resham', client: 'HOUSE OF RESHAM', tagline: 'Pret. Diffusion. Couture.', image: '/house-of-resham-new.jpg', industry: 'FASHION' },
    { slug: 'bagheera', client: 'THE BAGHEERA', tagline: 'Heritage Luxury Redefined', image: '/bagheera-heritage-guests.jpg', industry: 'HOSPITALITY' },
    { slug: 'garden-leaf', client: 'THE GARDEN LEAF', tagline: 'Nature Meets Gastronomy', image: '/garden-leaf-pasta.jpg', industry: 'HOSPITALITY' },
    { slug: 'focus-apparels', client: 'FOCUS APPARELS', tagline: 'Style Redefined', image: '/focus-apparels-new.jpg', industry: 'FASHION' },
    { slug: 'golden-locks', client: 'GOLDEN LOCKS', tagline: 'Architectural Excellence', image: '/goldenlocks-railing.jpg', industry: 'PREMIUM HARDWARE' }
  ];

  const influencerEvents = [
    { id: 1, image: '/influencer-meridien-1.jpg', title: 'Le Meridien Influencer Meet', location: 'Jaipur' },
    { id: 2, image: '/influencer-meridien-2.jpg', title: 'Le Meridien Heritage Experience', location: 'Jaipur' },
    { id: 3, image: '/influencer-holiday-1.jpg', title: 'Holiday Inn Blogger Meet', location: 'Jaipur' },
    { id: 4, image: '/influencer-holiday-2.jpg', title: 'Holiday Inn Food Festival', location: 'Jaipur' }
  ];

  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="casestudies-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">PORTFOLIO</span>
              <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 uppercase">
                OUR WORK
              </h1>
              <p className="text-xl text-white/60 max-w-3xl mx-auto">
                Real results. Real impact. Real growth for brands across industries.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-gray-50" data-testid="casestudies-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">CASE STUDIES</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
                CLIENT SUCCESS <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">STORIES</span>
              </h2>
              <p className="text-gray-600 text-lg mt-4">Click to explore each project in detail</p>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((client, idx) => (
              <RevealOnScroll key={client.slug} delay={idx * 0.05}>
                <Link to={`/case-studies/${client.slug}`}>
                  <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                    <img 
                      src={client.image} 
                      alt={client.client}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-xs font-bold text-brand-blue uppercase tracking-wider mb-2 block">
                        {client.industry}
                      </span>
                      <h3 className="font-outfit text-2xl font-bold text-white mb-1 group-hover:bg-gradient-to-r group-hover:from-brand-blue group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all uppercase">
                        {client.client}
                      </h3>
                      <p className="text-white/60 text-sm">{client.tagline}</p>
                    </div>
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Influencer Marketing Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="influencer-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-tr from-brand-blue/10 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">INFLUENCER MARKETING</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white uppercase">
                CURATED <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">EXPERIENCES</span>
              </h2>
              <p className="text-white/60 text-lg mt-4">Brand activations with top influencers</p>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {influencerEvents.map((event, idx) => (
              <RevealOnScroll key={event.id} delay={idx * 0.05}>
                <div className="relative overflow-hidden rounded-2xl group">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={event.image} 
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <p className="text-white font-semibold text-sm uppercase">{event.title}</p>
                    <p className="text-brand-blue text-xs uppercase">{event.location}</p>
                  </div>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <RevealOnScroll delay={0.2}>
            <div className="mt-12 text-center">
              <p className="text-white/50 text-lg mb-3 uppercase">COLLABORATED WITH</p>
              <p className="text-white font-semibold text-xl">LE MERIDIEN • HOLIDAY INN • CLARKS AMER • ITC HOTELS</p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-gray-50" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-16">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">CLIENTS</span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-gray-900 uppercase">
                TRUSTED BY <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">INDUSTRY LEADERS</span>
              </h2>
              <p className="text-gray-600 text-lg mt-4">50+ premium brands across hospitality, fashion, education & more</p>
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.1}>
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-12">
              <img 
                src="/client-logos-all-new.png" 
                alt="Zenith Marketing India Clients" 
                className="w-full h-auto"
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-brand-blue via-cyan-500 to-brand-blue relative overflow-hidden" data-testid="casestudies-cta">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RevealOnScroll>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6 uppercase">
              READY TO WRITE YOUR SUCCESS STORY?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's create measurable results for your brand.
            </p>
            <Link
              to="/contact"
              data-testid="casestudies-contact-btn"
              className="inline-flex items-center px-10 py-4 rounded-full bg-white text-brand-blue font-semibold hover:bg-gray-100 transition-all duration-300 uppercase"
            >
              START YOUR JOURNEY
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </RevealOnScroll>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
